import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { ParsedUrlQuery } from "querystring";
import styled from '@mui/system/styled';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import SearchHeading from '@/components/fasce/SearchHeading';
import SearchResultGroup from '@/components/fasce/SearchResultGroup';
import { WPSearchHeadingMode } from '@/data/wp/operations/queries/fragments/fascia-search-heading';
import { goToSearchResultPage, SEARCH_QS_PARAM } from '@/utils/router';
import { ActionKind, useAsyncSearch } from '@/utils/search';

const StyledCategories = styled(Stack)({
  marginBottom: "50px",
});

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  flexWrap: 'wrap',
  '& .MuiToggleButtonGroup-grouped': {
    margin: '7px 14px',
    padding: '17px',
    border: 0,
    '&.MuiToggleButton-root span': {
      display: 'inline-block',
      marginLeft: '60px',
      fontWeight: 'normal',
    },
    '&.MuiToggleButton-standard': {
      background: '#f1f1f1',
      color: '#182641',
      fontSize: '16px',
      lineHeight: '20px',
    },
    '&.Mui-selected': {
      background: '#182641',
      color: '#fff',
    },
    '&.MuiToggleButton-standard:hover': {
      background: '#182641',
      color: '#fff',
    },
    '&.Mui-disabled': {
      border: 0,
    },
    '&:not(:first-of-type)': {
      borderRadius: theme.shape.borderRadius,
    },
    '&:first-of-type': {
      marginLeft: 0,
      borderRadius: theme.shape.borderRadius,
    },
  },
}));

const getQSSearchTerm = (routerQuery: ParsedUrlQuery) => {
  if (routerQuery) {
    const val = routerQuery[SEARCH_QS_PARAM];
    if (Array.isArray(val)) {
      if (val.length > 0) {
        return val[0];
      } else {
        return null;
      }
    } else {
      return val || null;
    }
  } else {
    return null;
  }
}

/**
 * Mostra la schermata di risultato ricerca (con SearchHeading, Filtri per tipologia, Elenco raggruppato per tipologia).
 */
const SearchResult: React.FC = () => {
  const router = useRouter();
  const { t } = useTranslation('common');
  const [state, dispatch] = useAsyncSearch();
  const [selectedType, setSelectedType] = useState<string | null>(null);

  useEffect(() => {
    const text = getQSSearchTerm(router.query);
    if (text && text.length >= 3) {
      dispatch({ type: ActionKind.SEARCH, payload: text });
    }
  }, [router.query, dispatch]);

  const searchCallback = (text: string) => {
    goToSearchResultPage(router, text);
  }

  const handleFilterChange = (_: any, value: string | null) => {
    setSelectedType(value);
  };

  const showAllCallback = (type: string) => {
    setSelectedType(type);
  }

  const itemCount = (state.results.products.total || 0) + (state.results.projects.total || 0) + (state.results.contents.total || 0)

  return <Box>
    <SearchHeading
      mode={WPSearchHeadingMode.backend}
      searchCallback={searchCallback}
      title={t('search-result-heading-title-done', {}, {default: 'You searched for'})}
      query={state.query}
      numberResult={itemCount}
    />

    <Box sx={{ py: "60px" }}>
      <Container fixed sx={{ padding: '0 16px' }}>
        <StyledCategories direction="row" spacing={2} justifyContent="space-between" alignItems="end">
          <StyledToggleButtonGroup
              exclusive
              value={selectedType}
              onChange={handleFilterChange}
          >
            {Object.keys(state.results).map((type: string, index: number) => {
              const typeState = state.results[type];
              const label = t(`search-result-type-${type}`, {}, {default: type});
              return <ToggleButton key={index} value={type} size="large" disableRipple={true} selected={selectedType === type} sx={{opacity: typeState.pending ? 0.6: 1}}>
                {label} <span>{(typeState.items && typeState.total) || 0}</span>
              </ToggleButton>
            })}
          </StyledToggleButtonGroup>
        </StyledCategories>
      </Container>

      {Object.keys(state.results).map((type: string, index: number) => {
        if (selectedType === null || selectedType === type) {
          const typeState = state.results[type];
          const title = t(`search-result-type-${type}`, {}, {default: type});
          return <SearchResultGroup key={index} type={type} title={title} showAll={selectedType === type} items={typeState.items} total={typeState.total} pending={typeState.pending} showAllCallback={showAllCallback} />
        }
      })}
    </Box>
  </Box>
}

export default SearchResult;

